import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/context/AdminAuthContext";
import { Card, PageTitle, Btn, Empty, Field, Input, Textarea } from "@/admin/ui";

const CATEGORIES = ["Products", "Quality & Safety", "Orders & Shipping", "Payments & Refunds", "Partnerships", "Sustainability", "Usage & Wellness"];

const FaqForm = ({ faq, onSaved, onCancel }) => {
  const [form, setForm] = useState({ category: faq?.category || CATEGORIES[0], q: faq?.q || "", a: faq?.a || "", order: faq?.order ?? 99 });
  const [busy, setBusy] = useState(false);
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      faq?.id ? await adminApi.put(`/admin/faqs/${faq.id}`, payload) : await adminApi.post("/admin/faqs", payload);
      toast.success("FAQ saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="faq-form" onSubmit={save} className="space-y-4">
      <div className="grid sm:grid-cols-[1fr_120px] gap-4">
        <Field label="Category">
          <Input data-testid="faq-category-input" list="faq-cats" value={form.category} onChange={on("category")} required />
          <datalist id="faq-cats">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label="Order"><Input data-testid="faq-order-input" type="number" value={form.order} onChange={on("order")} /></Field>
      </div>
      <Field label="Question"><Input data-testid="faq-question-input" value={form.q} onChange={on("q")} required /></Field>
      <Field label="Answer"><Textarea data-testid="faq-answer-input" rows={4} value={form.a} onChange={on("a")} required /></Field>
      <div className="flex justify-end gap-2">
        <Btn type="button" variant="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn type="submit" busy={busy} data-testid="faq-form-save">Save FAQ</Btn>
      </div>
    </form>
  );
};

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => adminApi.get("/admin/faqs").then(({ data }) => setFaqs(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (f) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try { await adminApi.delete(`/admin/faqs/${f.id}`); toast.success("FAQ deleted"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  if (editing !== null) {
    return (
      <div>
        <PageTitle title={editing?.id ? "Edit FAQ" : "New FAQ"} />
        <Card className="p-6 sm:p-8"><FaqForm faq={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /></Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-faqs-page">
      <PageTitle title="FAQs" sub="Questions shown on the FAQ page, grouped by category." action={<Btn data-testid="add-faq-button" onClick={() => setEditing({})}><Plus size={14} /> Add FAQ</Btn>} />
      <Card className="overflow-hidden">
        {faqs.length === 0 && <Empty text="No FAQs yet." />}
        <ul className="divide-y divide-forest/10">
          {faqs.map((f) => (
            <li key={f.id} data-testid={`admin-faq-row-${f.id}`} className="flex items-start gap-4 px-4 sm:px-6 py-4">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold">{f.category}</p>
                <p className="text-sm font-semibold text-forest mt-0.5">{f.q}</p>
                <p className="text-xs text-mutedgreen mt-1 line-clamp-2">{f.a}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`edit-faq-${f.id}`} onClick={() => setEditing(f)} aria-label="Edit" className="p-2 rounded-full text-forest hover:bg-forest/5"><Pencil size={15} /></button>
                <button data-testid={`delete-faq-${f.id}`} onClick={() => remove(f)} aria-label="Delete" className="p-2 rounded-full text-mutedgreen hover:text-red-700 hover:bg-red-50"><Trash2 size={15} /></button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
